/**
 * Long-form user guides shown at /help/guides/:slug
 *
 * These are richer than the per-module help articles — they are designed to
 * be copy/pasted into an email or a Word document.
 *
 * Block types:
 *   { type: 'p',         text }                 paragraph
 *   { type: 'h3',        text }                 sub-heading inside a section
 *   { type: 'list',      items: [string] }      bullet list
 *   { type: 'steps',     items: [string] }      numbered list
 *   { type: 'tip',       text }                 callout (💡)
 *   { type: 'warn',      text }                 callout (⚠️)
 *   { type: 'kbd',       text }                 inline keyboard hint
 *   { type: 'screenshot',caption, hint? }       placeholder box for a screenshot
 *
 * To add a screenshot: replace the placeholder image manually after export,
 * or — if you want it baked-in — drop a PNG under client/src/assets/guides/
 * and add `src: '/static/guides/foo.png'` to the screenshot block.
 */

export const USER_GUIDES = {
  // ────────────────────────────────────────────────────────────────
  // AGENT
  // ────────────────────────────────────────────────────────────────
  agent: {
    slug: 'agent',
    title: 'Agent User Guide',
    audience: ['agent', 'supervisor', 'admin'],
    subtitle:
      'How to sign in, take and make calls with the WebRTC softphone, and use hold, transfer, and conference.',
    sections: [
      {
        heading: '1. Signing in',
        body: [
          { type: 'p', text:
            'ContinuumCX runs entirely in your browser. Open https://cx.continuumgbl.com in Chrome or Edge, enter the email and password your administrator gave you, then complete MFA if prompted.' },
          { type: 'screenshot', caption: 'Login page',
            hint: 'Paste a screenshot of the cx.continuumgbl.com login screen here.' },
          { type: 'tip', text:
            'The first time you sign in, the browser will ask for microphone permission. Click Allow — without it the softphone cannot send your voice.' },
        ],
      },
      {
        heading: '2. The Softphone tab',
        body: [
          { type: 'p', text:
            'After login you land on the Softphone. It registers automatically — you do not type an extension or SIP password. Within a few seconds the status bar shows "Not Ready", which means you are signed into the system but NOT in any queue yet.' },
          { type: 'screenshot', caption: 'Softphone — Not Ready state',
            hint: 'Take a screenshot of the full softphone card immediately after login, while the status bar shows Not Ready.' },
          { type: 'warn', text:
            'Keep this tab open. If you close it you are disconnected from every queue.' },
        ],
      },
      {
        heading: '3. Going Ready / Not Ready / AUX / Wrap',
        body: [
          { type: 'p', text:
            'The leftmost pill on the status bar is your ACD state. Click it to switch between the states below:' },
          { type: 'list', items: [
            '○ Not Ready — you are NOT in any queue. Calls will not be routed to you.',
            '✓ Ready (green) — you are in your assigned queues and will receive calls.',
            '⏸ AUX (paused) — temporarily out, with a reason: Lunch, Break, Meeting, Training, Admin, Other.',
            '📝 Wrap — post-call wrap-up. Auto-flips back to Ready when the queue’s wrap timer ends.',
            '🚪 Logout — removes you from every queue and unregisters the softphone.',
          ]},
          { type: 'screenshot', caption: 'State menu',
            hint: 'Click the state pill so the dropdown opens (Ready / AUX reasons / Logout) and take a screenshot.' },
          { type: 'tip', text:
            'When a supervisor force-pauses or force-logs you out, a notice appears in the status bar.' },
        ],
      },
      {
        heading: '4. Receiving a call from a queue',
        body: [
          { type: 'steps', items: [
            'You hear a short ring (or, on auto-answer queues, the call connects automatically after a single beep).',
            'A short whisper announcement may play to you only, telling you which queue the call came from. The customer hears nothing during the whisper.',
            'The call timer starts in the status bar. The status pill changes to "On call".',
            'When the customer hangs up — or you click 📞 Hangup — you go into Wrap. The wrap-up countdown is shown in the status bar.',
          ]},
          { type: 'screenshot', caption: 'Softphone — On Call',
            hint: 'Take a screenshot during a live test call so the active call panel, timer and in-call controls are visible.' },
        ],
      },
      {
        heading: '5. Making an outbound call',
        body: [
          { type: 'steps', items: [
            'Type the number in the dial input at the top of the softphone (extension or PSTN number — country prefix optional depending on dial-plan).',
            'Click 📞 Dial.',
            'Use ☰ Dialpad once the call is connected to send DTMF tones (e.g. menu navigation).',
            'Click ↺ Redial to call the last number you dialed.',
          ]},
          { type: 'screenshot', caption: 'Dial input + Dialpad',
            hint: 'Screenshot the dial bar with a number entered, and a separate screenshot of the DTMF dialpad expanded.' },
        ],
      },
      {
        heading: '6. In-call controls — Mute, Hold, Hang up',
        body: [
          { type: 'p', text:
            'The control row at the bottom of the softphone is always visible while a call is active.' },
          { type: 'list', items: [
            '🔇 Mute / 🔊 Unmute — silences your microphone. The customer hears music or silence depending on dial-plan.',
            '⏸ Hold / ▶ Unhold — puts the customer on hold with music on hold (MOH). The timer continues to count.',
            '📞 Hangup — ends the call. You enter Wrap.',
          ]},
          { type: 'screenshot', caption: 'In-call controls bar',
            hint: 'Take a close-up screenshot of the mute / hold / transfer / conference / hangup / dialpad row.' },
        ],
      },
      {
        heading: '7. Transfer — blind and attended',
        body: [
          { type: 'p', text:
            'Click ↗ Transfer to open the Transfer dialog. Type a destination — another agent extension, a queue ID, or a PSTN number — and pick a mode:' },
          { type: 'list', items: [
            'Blind — the call is sent to the destination immediately. You drop out of the call.',
            'Attended — you talk to the destination first while the customer is on hold. If they accept, click "Complete transfer" and you drop out. If they refuse, click "Cancel" and you are brought back to the customer.',
          ]},
          { type: 'screenshot', caption: 'Transfer dialog',
            hint: 'Open the Transfer dialog during a live test call (or use a screenshot of the form when no call is active) and capture it.' },
          { type: 'tip', text:
            'Attended transfer is the safe default — it lets you brief the receiving agent before the customer is handed over.' },
        ],
      },
      {
        heading: '8. Conference — bring a third party into the call',
        body: [
          { type: 'steps', items: [
            'Click 👥 Conference during the call.',
            'Type the third-party destination (extension or PSTN).',
            'They are dialed and added into a ConfBridge with you and the customer. Music on hold stops once they answer.',
            'To leave the conference yourself, click 📞 Hangup — the remaining parties stay connected.',
            'The conference room is automatically cleaned up when the last party leaves.',
          ]},
          { type: 'screenshot', caption: 'Conference dialog',
            hint: 'Take a screenshot of the Conference modal with a destination typed in.' },
        ],
      },
      {
        heading: '9. Call quality (QoS) panel',
        body: [
          { type: 'p', text:
            'Click 📶 (top-right of the softphone) to open the QoS panel. It refreshes every 2 seconds and shows MOS, jitter, round-trip time and packet loss.' },
          { type: 'screenshot', caption: 'QoS panel — expanded',
            hint: 'During a live test call, expand the QoS panel and screenshot it so all four metrics are visible.' },
          { type: 'list', items: [
            'MOS rating: Excellent / Good / Fair / Poor / Bad — under Fair, raise the issue with your supervisor.',
            'Jitter under 30 ms is good.',
            'Round-trip time under 200 ms is good.',
            'Packet loss under 0.5% is good.',
          ]},
        ],
      },
      {
        heading: '10. Audio settings',
        body: [
          { type: 'p', text:
            'Click ⚙️ Settings in the softphone to pick your microphone, speaker and ring device. The Audio test plays a short sound through the selected speaker so you can confirm the right device is selected before you take real calls.' },
          { type: 'screenshot', caption: 'Settings — audio devices',
            hint: 'Open the Settings panel with the device dropdowns expanded and screenshot it.' },
        ],
      },
      {
        heading: '11. ACD Personal view',
        body: [
          { type: 'p', text:
            'From the sidebar, open ACD → Personal. You see your current state, time-in-state, handled calls today and the queues you are assigned to.' },
          { type: 'screenshot', caption: 'ACD Personal view',
            hint: 'Navigate to ACD → Personal and take a full-page screenshot.' },
        ],
      },
      {
        heading: '12. Troubleshooting',
        body: [
          { type: 'list', items: [
            'No audio after answering — open Settings, confirm the right microphone/speaker is selected, then reload the tab.',
            'Calls do not ring you — confirm the status bar shows ✓ Ready and that you are assigned to a queue (Admin → Agents).',
            'You got force-logged out — sign in again. Check with your supervisor whether they triggered it.',
            'Sudden disconnect — your laptop may have slept, or the network dropped. Sign in again.',
            'Poor MOS — switch to a wired connection or move closer to your Wi-Fi access point. Screenshot the QoS panel and send it to your supervisor.',
          ]},
        ],
      },
      {
        heading: '13. Your account',
        body: [
          { type: 'p', text:
            'From the sidebar, open Account. You can change your password, toggle light/dark theme, and sign out cleanly.' },
          { type: 'screenshot', caption: 'Account page',
            hint: 'Navigate to Account and screenshot the full page.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // SUPERVISOR
  // ────────────────────────────────────────────────────────────────
  supervisor: {
    slug: 'supervisor',
    title: 'Supervisor User Guide',
    audience: ['supervisor', 'admin'],
    subtitle:
      'How to monitor the floor, use Supervisor Tools (Listen / Whisper / Barge / Force / Broadcast), play recordings and run reports.',
    sections: [
      {
        heading: '1. Signing in as a supervisor',
        body: [
          { type: 'p', text:
            'Open https://cx.continuumgbl.com and sign in. Supervisors land on the Wallboard by default if the Softphone module is granted; otherwise on the first module you have access to.' },
          { type: 'screenshot', caption: 'Login + landing page',
            hint: 'Capture a screenshot of the login page and the first screen you see after signing in.' },
          { type: 'tip', text:
            'You need an extension to use Listen / Whisper / Barge. If you do not have one, ask an admin to assign you one in Admin → Agents.' },
        ],
      },
      {
        heading: '2. Wallboard — live floor view',
        body: [
          { type: 'p', text:
            'ACD → Wallboard refreshes every 5 seconds. It shows two rows of KPI cards, a pie chart of agent states, and a stacked bar chart with the per-queue table below it.' },
          { type: 'screenshot', caption: 'Wallboard — full page',
            hint: 'Take a full-page screenshot of the Wallboard while there is some traffic so the KPIs are non-zero.' },
          { type: 'list', items: [
            'Row 1 — Calls in queue · Longest wait · SLA (overall) · Agents available.',
            'Row 2 — Offered today · Answered today · Abandoned today · Occupancy.',
            'KPI cards flash red when a metric crosses a threshold (e.g. longest wait > 30s, occupancy ≥ 85%).',
          ]},
          { type: 'h3', text: 'TV mode' },
          { type: 'p', text:
            'Click ⛶ Full Screen for a dashboard-style view suitable for a wall-mounted TV. Press Esc to leave.' },
          { type: 'screenshot', caption: 'Wallboard — TV mode',
            hint: 'Press ⛶ Full Screen, take a screenshot, then press Esc.' },
          { type: 'h3', text: 'Tenant filter' },
          { type: 'p', text:
            'If you are a Continuum supervisor you see a "Client" dropdown top-right that limits everything on the page to one tenant. Tenant-bound supervisors don’t see this — they are already scoped automatically.' },
        ],
      },
      {
        heading: '3. Queues view — detail per queue',
        body: [
          { type: 'p', text:
            'ACD → Queues shows one card per portal-managed queue: callers waiting (with wait time), members (with state and per-queue stats), and per-queue counters.' },
          { type: 'screenshot', caption: 'Queues view',
            hint: 'Navigate to ACD → Queues and take a full-page screenshot.' },
          { type: 'list', items: [
            'Filter by queue, agent (extension or name), skill, or DID.',
            'Each member card shows their current state, time-in-state, and the calls handled today.',
          ]},
        ],
      },
      {
        heading: '4. Supervisor Tools — live agent management',
        body: [
          { type: 'p', text:
            'ACD → Supervisor Tools is the cockpit for real-time agent management. The agent list refreshes every 5 seconds and is tenant-scoped.' },
          { type: 'screenshot', caption: 'Supervisor Tools — agent list',
            hint: 'Take a full-page screenshot of ACD → Supervisor Tools with the agent table populated.' },
          { type: 'h3', text: '🎧 Listen (silent monitor)' },
          { type: 'p', text:
            'Click 🎧 Listen on an agent currently on a call. Your softphone tab auto-opens and auto-answers within 1–2 seconds. You hear both sides; neither party hears you. Click 📞 Hangup to stop — the agent and customer see nothing change.' },
          { type: 'h3', text: '💬 Whisper' },
          { type: 'p', text:
            'Like Listen, but the agent also hears you. The customer hears only the agent. Use it to coach during a live call.' },
          { type: 'h3', text: '🤝 Barge' },
          { type: 'p', text:
            'Three-way: you, the agent, and the customer all hear each other. The confirm dialog protects against accidental clicks.' },
          { type: 'warn', text:
            'Barge interrupts the conversation — confirm before you click it.' },
          { type: 'screenshot', caption: 'Listen / Whisper / Barge buttons',
            hint: 'Hover over an On Call agent so the spy buttons appear; take a close-up screenshot.' },
          { type: 'h3', text: '⏸ Force Pause / 🚪 Force Logout' },
          { type: 'p', text:
            'Force Pause sends the agent to AUX with a reason you supply (e.g. "training"). Force Logout removes them from every queue and disconnects their softphone. Both actions notify the agent in real time and are audit-logged.' },
          { type: 'screenshot', caption: 'Force Pause modal',
            hint: 'Click ⏸ Force Pause on an agent and screenshot the reason dropdown.' },
          { type: 'h3', text: 'Broadcasts' },
          { type: 'p', text:
            'Click Broadcast to send a short notice to all agents (or a queue subset). Pick a severity — Info / Warning / Major / Critical — and an optional title. Agents see a banner on the softphone and dismiss it; the dismissal is recorded.' },
          { type: 'screenshot', caption: 'Broadcast composer',
            hint: 'Open the Broadcast dialog and screenshot the form with a sample message.' },
        ],
      },
      {
        heading: '5. Recordings',
        body: [
          { type: 'p', text:
            'Open Recordings from the sidebar. You see calls ingested from the active ACD — tenant-scoped to the queues you own.' },
          { type: 'screenshot', caption: 'Recordings — filter + result list',
            hint: 'Take a screenshot of the Recordings page with the filters set and results visible.' },
          { type: 'list', items: [
            'Filter by date (Today / This week / This month / Custom), queue, agent extension, or direction.',
            'Click ▶ to stream inline (no download).',
            'Click ⤓ Download to grab a local WAV/MP3.',
            'Archived recordings (in Azure Blob) stream the same way — you do not have to know whether the file is local or archived.',
          ]},
          { type: 'screenshot', caption: 'Recording playback',
            hint: 'Click play on a recording and screenshot the inline audio player.' },
        ],
      },
      {
        heading: '6. Reports',
        body: [
          { type: 'p', text:
            'Reports → choose a tab from the left or the top tabs:' },
          { type: 'list', items: [
            'Agent productivity — calls handled, AHT, talk time per agent.',
            'Queue performance — offered, answered, abandoned, ASA, % answered.',
            'CDR drill-down — every call with filters; click the colored Disconnect pill for a full CEL trace.',
            'Trunks — Telnyx and other trunks: count, ASR, average call duration.',
          ]},
          { type: 'screenshot', caption: 'Reports — Queue performance',
            hint: 'Open Reports → Queues, pick "This week" and screenshot the table + chart.' },
          { type: 'h3', text: 'Time presets' },
          { type: 'list', items: [
            'Today / Yesterday / This week / This month / Last 7d / Last 30d.',
            'Custom — pick start and end dates (max 90 days).',
            'The "Today" preset auto-refreshes every 30 seconds. Other presets refresh on demand — click ↻.',
          ]},
          { type: 'h3', text: 'CDR drill-down — Disconnect trace' },
          { type: 'p', text:
            'In the CDR table every row shows who hung up first — Caller / Agent / No connect — with "(inferred)" because that label is the fast heuristic. Click the pill to open the side panel with the authoritative CEL trace: per-leg timestamps, full event ladder, and which channel left the bridge first.' },
          { type: 'screenshot', caption: 'CDR drill-down — CEL trace side panel',
            hint: 'In Reports → CDR, click a colored Disconnect pill and screenshot the open side panel.' },
          { type: 'h3', text: 'Export' },
          { type: 'p', text:
            'Click ⤓ CSV for a spreadsheet copy, or ⤓ PDF for a branded landscape PDF.' },
        ],
      },
      {
        heading: '7. Troubleshooting',
        body: [
          { type: 'list', items: [
            'Listen / Whisper / Barge returns "supervisor_has_no_extension" — ask an admin to assign you an extension in Admin → Agents.',
            'Agent list looks empty — check the tenant filter at the top of the page; you may be scoped to a tenant with no current activity.',
            'Recordings list is empty — confirm the date filter, and that recordings have been ingested (Admin → Recording).',
            'A report seems stale — click ↻ to force a refresh, or switch off "Today" auto-refresh and back on.',
          ]},
        ],
      },
      {
        heading: '8. Your account',
        body: [
          { type: 'p', text:
            'From the sidebar, open Account to change your password, toggle light/dark theme, or sign out.' },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────
  // VIEWER (client read-only)
  // ────────────────────────────────────────────────────────────────
  viewer: {
    slug: 'viewer',
    title: 'Viewer User Guide',
    audience: ['viewer', 'admin'],
    subtitle:
      'Read-only access for clients: how to monitor your dashboards, listen to your recordings, and run your reports.',
    sections: [
      {
        heading: '1. What the Viewer role can do',
        body: [
          { type: 'p', text:
            'The Viewer role is read-only. It is intended for clients who want visibility into their own ContinuumCX activity without taking calls themselves.' },
          { type: 'list', items: [
            '✓ See live dashboards for your queues (ACD → Wallboard, Queues).',
            '✓ Play and download recordings for your queues.',
            '✓ Run reports — Agent productivity, Queue performance, CDR drill-down, Trunks.',
            '✗ No softphone — you cannot take or place calls.',
            '✗ No agent control — you cannot pause, log out or message agents.',
            '✗ No admin or settings access.',
          ]},
          { type: 'tip', text:
            'Everything you see is automatically scoped to your tenant — you only see your own queues, your own agents, and your own recordings.' },
        ],
      },
      {
        heading: '2. Signing in',
        body: [
          { type: 'p', text:
            'Open https://cx.continuumgbl.com and sign in with the email and temporary password your account manager sent you. You will be forced to change the password on first login. After login you land on the Wallboard.' },
          { type: 'screenshot', caption: 'Login page',
            hint: 'Take a screenshot of the cx.continuumgbl.com login screen.' },
          { type: 'screenshot', caption: 'First view after login (Wallboard)',
            hint: 'Take a full-page screenshot of the Wallboard immediately after signing in.' },
        ],
      },
      {
        heading: '3. Wallboard — your real-time dashboard',
        body: [
          { type: 'p', text:
            'ACD → Wallboard refreshes every 5 seconds and shows the activity of your queues.' },
          { type: 'list', items: [
            'Two rows of KPI cards: calls in queue, longest wait, SLA, agents available; then offered / answered / abandoned today and occupancy.',
            'Agent state pie: how many agents are Ready, AUX, Wrap, On Call, or Not Ready.',
            'Per-queue chart and table: waiting / done / abandoned per queue, with today’s totals and SLA.',
          ]},
          { type: 'screenshot', caption: 'Wallboard — KPIs + charts',
            hint: 'Take a screenshot of the Wallboard during active hours so the cards and charts are populated.' },
          { type: 'h3', text: 'TV mode for a meeting room or screen' },
          { type: 'p', text:
            'Click ⛶ Full Screen for a dashboard-style view that scales nicely on a TV. Press Esc to exit.' },
          { type: 'screenshot', caption: 'Wallboard — TV mode',
            hint: 'Press ⛶ Full Screen and screenshot the result.' },
        ],
      },
      {
        heading: '4. Queues view',
        body: [
          { type: 'p', text:
            'ACD → Queues shows one card per queue you own. You see callers currently waiting (with wait time) and the agents assigned to each queue with their live state.' },
          { type: 'screenshot', caption: 'Queues view',
            hint: 'Navigate to ACD → Queues and take a full-page screenshot.' },
        ],
      },
      {
        heading: '5. Listening to recordings',
        body: [
          { type: 'steps', items: [
            'Open Recordings from the sidebar.',
            'Pick a date range — Today / This week / This month / Custom.',
            'Optionally filter by queue, agent extension or direction.',
            'Click ▶ to stream the call inline in your browser.',
            'Click ⤓ Download to save a local WAV/MP3 copy.',
          ]},
          { type: 'screenshot', caption: 'Recordings — filter + result list',
            hint: 'Take a screenshot of the Recordings page with filters and a few results visible.' },
          { type: 'screenshot', caption: 'Inline playback',
            hint: 'Click ▶ on a recording and screenshot the inline audio player.' },
          { type: 'tip', text:
            'Archived recordings (older calls moved to cloud storage) stream exactly the same way — no extra step needed.' },
        ],
      },
      {
        heading: '6. Running reports',
        body: [
          { type: 'p', text:
            'Open Reports from the sidebar. Four tabs are available:' },
          { type: 'list', items: [
            'Agent productivity — calls handled, average handle time, talk time per agent (your tenant).',
            'Queue performance — offered, answered, abandoned, ASA, % answered per queue.',
            'CDR drill-down — every individual call with filters; click the Disconnect pill for a full event trace.',
            'Trunks — count, ASR and average call duration per trunk.',
          ]},
          { type: 'screenshot', caption: 'Reports — Queue performance',
            hint: 'Open Reports → Queues, pick "This week" and screenshot the table and chart.' },
          { type: 'h3', text: 'Picking a time range' },
          { type: 'list', items: [
            'Today / Yesterday / This week / This month / Last 7d / Last 30d.',
            'Custom — pick start and end (max 90 days).',
            'The "Today" preset auto-refreshes every 30 seconds; other ranges refresh on demand with ↻.',
          ]},
          { type: 'h3', text: 'Exporting' },
          { type: 'list', items: [
            '⤓ CSV — spreadsheet-ready download.',
            '⤓ PDF — branded landscape PDF for sharing with stakeholders.',
          ]},
          { type: 'screenshot', caption: 'Export buttons',
            hint: 'Capture a close-up of the CSV / PDF buttons in any report.' },
        ],
      },
      {
        heading: '7. CDR drill-down — investigating a single call',
        body: [
          { type: 'p', text:
            'In Reports → CDR each row represents one call: source, destination, queue, agent, duration, and a colored Disconnect pill showing who hung up first.' },
          { type: 'p', text:
            'Click any pill to open the side panel — it shows the authoritative event-by-event trace of the call: each leg, when it started/stopped, who left the bridge first, and the final disposition. Useful for client-side QA.' },
          { type: 'screenshot', caption: 'CDR drill-down — side panel',
            hint: 'In Reports → CDR, click a Disconnect pill and screenshot the open side panel with the timeline.' },
        ],
      },
      {
        heading: '8. Account',
        body: [
          { type: 'p', text:
            'Open Account from the sidebar to change your password, toggle light/dark theme, or sign out.' },
          { type: 'screenshot', caption: 'Account page',
            hint: 'Take a screenshot of the Account page.' },
        ],
      },
      {
        heading: '9. Need help?',
        body: [
          { type: 'p', text:
            'Every module has a ❓ Help button in the top-right corner that opens a short in-app guide for that specific page. For account or access questions, contact your Continuum account manager.' },
        ],
      },
    ],
  },
};

export function getUserGuide(slug) {
  return USER_GUIDES[slug] || null;
}

export function listUserGuides() {
  return Object.values(USER_GUIDES);
}
